// Types pour Generic Sensor API
interface AccelerometerOptions {
  frequency?: number;
}

interface Accelerometer extends EventTarget {
  readonly x: number | null;
  readonly y: number | null;
  readonly z: number | null;
  start(): void;
  stop(): void;
}

declare global {
  interface Window {
    Accelerometer?: {
      new (options?: AccelerometerOptions): Accelerometer;
    };
  }
  const Accelerometer: {
    new (options?: AccelerometerOptions): Accelerometer;
  } | undefined;
}

export interface MotionData {
  ax: number;    // m/s²
  ay: number;    // m/s²
  az: number;    // m/s²
  magnitude: number; // |a| = sqrt(x²+y²+z²)
  accelHz: number;   // fréquence échantillonnage
  available: boolean; // capteur disponible
}

export interface MotionOptions {
  onMotion: (data: MotionData) => void;
  onMotionPermissionNeeded?: () => void;
  frequency?: number;           // Hz souhaité (Generic Sensor)
  windowSize?: number;         // taille fenêtre lissage
}

class MotionManager {
  private options: Required<MotionOptions>;
  private accel: Accelerometer | null = null;
  private motionAvailable = false;
  private motionPermissionGranted = false;
  private timestamps: number[] = [];
  private smoothingBuffers: { ax: number[]; ay: number[]; az: number[] };
  private currentMotion: MotionData = {
    ax: 0, ay: 0, az: 0,
    magnitude: 0,
    accelHz: 0,
    available: false
  };
  private deviceMotionListener: ((event: DeviceMotionEvent) => void) | null = null;

  constructor(options: MotionOptions) {
    this.options = {
      frequency: 60,
      windowSize: 5,
      onMotionPermissionNeeded: () => {},
      ...options
    };
    
    this.smoothingBuffers = {
      ax: [],
      ay: [],
      az: []
    };

    this.initializeMotionSensor();
  }

  private async initializeMotionSensor() {
    // Essai Generic Sensor API en priorité
    if ('Accelerometer' in window && window.Accelerometer) {
      try {
        const accel = new window.Accelerometer({ frequency: this.options.frequency });
        
        accel.addEventListener('reading', () => {
          if (accel.x !== null && accel.y !== null && accel.z !== null) {
            this.processAcceleration(accel.x, accel.y, accel.z, Date.now());
          }
        });

        accel.addEventListener('error', () => {
          console.log('Generic Sensor error, fallback to DeviceMotion');
          this.fallbackToDeviceMotion();
        });

        accel.start();
        this.accel = accel;
        this.motionAvailable = true;
        this.motionPermissionGranted = true;
        this.currentMotion.available = true;
        
        console.log('Generic Sensor API actif');
        return;
      } catch {
        console.log('Generic Sensor non disponible, fallback DeviceMotion');
      }
    }

    // Fallback DeviceMotion
    this.fallbackToDeviceMotion();
  }

  private fallbackToDeviceMotion() {
    if ('DeviceMotionEvent' in window) {
      this.motionAvailable = true;

      // Vérification permission iOS
      if (typeof (DeviceMotionEvent as unknown as { requestPermission?: () => Promise<string> }).requestPermission === 'function') {
        // iOS - permission requise
        this.options.onMotionPermissionNeeded();
      } else {
        // Android/Desktop - démarrer directement
        this.startDeviceMotionListener();
        this.motionPermissionGranted = true;
      }
    } else {
      console.log('DeviceMotion non disponible');
      this.currentMotion.available = false;
    }
  }

  private startDeviceMotionListener() {
    this.deviceMotionListener = (event: DeviceMotionEvent) => {
      const acc = event.accelerationIncludingGravity;
      if (acc && acc.x !== null && acc.y !== null && acc.z !== null) {
        this.processAcceleration(acc.x, acc.y, acc.z, Date.now());
      }
    };

    window.addEventListener('devicemotion', this.deviceMotionListener);
    this.currentMotion.available = true;
  }

  async requestMotionPermission(): Promise<boolean> {
    if (!this.motionAvailable) return false;
    
    if (typeof (DeviceMotionEvent as unknown as { requestPermission?: () => Promise<string> }).requestPermission === 'function') {
      try {
        const permission = await (DeviceMotionEvent as unknown as { requestPermission: () => Promise<string> }).requestPermission();
        if (permission === 'granted') {
          this.motionPermissionGranted = true;
          this.startDeviceMotionListener();
          return true;
        }
      } catch (error) {
        console.error('Motion permission denied:', error);
      }
    }
    return this.motionPermissionGranted;
  }

  private processAcceleration(x: number, y: number, z: number, timestamp: number) {
    // Mise à jour fréquence (pattern de heading.ts)
    this.updateAccelHz(timestamp);

    // Lissage des valeurs
    const smoothed = this.applySmoothingIfNeeded(x, y, z);
    
    // Calcul magnitude |a| = sqrt(x²+y²+z²)
    const magnitude = Math.sqrt(smoothed.x * smoothed.x + smoothed.y * smoothed.y + smoothed.z * smoothed.z);

    this.currentMotion = {
      ax: Number(smoothed.x.toFixed(2)),
      ay: Number(smoothed.y.toFixed(2)),
      az: Number(smoothed.z.toFixed(2)),
      magnitude: Number(magnitude.toFixed(2)),
      accelHz: Number(this.currentMotion.accelHz.toFixed(1)),
      available: true
    };

    this.options.onMotion(this.currentMotion);
  }

  private applySmoothingIfNeeded(x: number, y: number, z: number): { x: number; y: number; z: number } {
    if (this.options.windowSize <= 1) {
      return { x, y, z }; // Pas de lissage
    }

    // Ajouter aux buffers
    this.smoothingBuffers.ax.push(x);
    this.smoothingBuffers.ay.push(y);
    this.smoothingBuffers.az.push(z);

    // Limiter taille des buffers
    if (this.smoothingBuffers.ax.length > this.options.windowSize) {
      this.smoothingBuffers.ax.shift();
      this.smoothingBuffers.ay.shift();
      this.smoothingBuffers.az.shift();
    }

    // Moyenne glissante
    return {
      x: this.smoothingBuffers.ax.reduce((sum, val) => sum + val, 0) / this.smoothingBuffers.ax.length,
      y: this.smoothingBuffers.ay.reduce((sum, val) => sum + val, 0) / this.smoothingBuffers.ay.length,
      z: this.smoothingBuffers.az.reduce((sum, val) => sum + val, 0) / this.smoothingBuffers.az.length
    };
  }

  private updateAccelHz(timestamp: number) {
    // Pattern identique à heading.ts updateGpsHz()
    this.timestamps.push(timestamp);
    
    const tenSecondsAgo = timestamp - 10000;
    this.timestamps = this.timestamps.filter(t => t > tenSecondsAgo);
    
    if (this.timestamps.length > 1) {
      const duration = (this.timestamps[this.timestamps.length - 1] - this.timestamps[0]) / 1000;
      this.currentMotion.accelHz = duration > 0 ? (this.timestamps.length - 1) / duration : 0;
    }
  }

  getCurrentMotion(): MotionData {
    return { ...this.currentMotion };
  }

  stop() {
    // Nettoyage Generic Sensor
    if (this.accel) {
      try {
        this.accel.stop();
      } catch (error) {
        console.log('Erreur arrêt Generic Sensor:', error);
      }
      this.accel = null;
    }

    // Nettoyage DeviceMotion listener
    if (this.deviceMotionListener) {
      window.removeEventListener('devicemotion', this.deviceMotionListener);
      this.deviceMotionListener = null;
    }

    // Reset état
    this.timestamps = [];
    this.smoothingBuffers = { ax: [], ay: [], az: [] };
  }
}

let motionManagerInstance: MotionManager | null = null;

export function startMotion(options: MotionOptions): { stop: () => void } {
  if (motionManagerInstance) {
    motionManagerInstance.stop();
  }
  
  motionManagerInstance = new MotionManager(options);
  
  return {
    stop: () => {
      if (motionManagerInstance) {
        motionManagerInstance.stop();
        motionManagerInstance = null;
      }
    }
  };
}

export function getMotionManager(): MotionManager | null {
  return motionManagerInstance;
}